import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackPhoneButtonClick, trackCall, trackShowWarning, trackAcceptWarning, trackCloseWarning } from './tracking';
import { fetchComagic } from '../../actions/comagic';
import { setNewbuildingCallValuationOpen, setOfferIdForCallback } from '../../actions/newbuildingCallback';
import { openNotAvailableModal } from '../../actions/notAvailableModal';
import { selectProductById } from '../../selectors/analytics';

import { defferedTrack } from '../../utils/analytics';
import { useApplicationContext } from '../../utils/applicationContext';
import { useChats } from '../useChats';
import { getCallData } from '../getCallData';
import { TRecommendation } from '../../types/recommendation';

export function useCall(offer: TRecommendation, onClick?: () => void) {
  const dispatch = useDispatch();
  const { logger, httpApi } = useApplicationContext();

  const {
    phone,
    gaLabel,
    siteBlockId,
    isCalltrackingEnabled,
    isLeadFactory,
    isDeveloper,
    agentAvailability,
    authorUserId,
  } = React.useMemo(() => getCallData(offer), [offer]);

  const product = useSelector(selectProductById(offer.offerId));

  const { isEnabled: isChatsEnabled } = useChats(offer);

  const produceCall = React.useCallback(
    (phone: string, forceCall = false) => {
      if (agentAvailability && !agentAvailability.available && isChatsEnabled && !forceCall) {
        dispatch(openNotAvailableModal({ offer, phone }));

        return;
      }
      window.location.href = `tel:${phone}`;
      defferedTrack(() => trackCall({ label: gaLabel, product }));

      const isValidAuthorId = typeof authorUserId === 'number';

      if (isDeveloper && isValidAuthorId) {
        const onFocusListener = () => {
          window.removeEventListener('focus', onFocusListener);

          dispatch(setOfferIdForCallback(offer.offerId));
          dispatch(setNewbuildingCallValuationOpen(true));
        };

        window.addEventListener('focus', onFocusListener);
      }
    },
    [agentAvailability, isChatsEnabled, gaLabel, product, authorUserId, isDeveloper, dispatch, offer],
  );

  const loadComagicPhone = React.useCallback(
    function () {
      return fetchComagic(
        { comagicId: siteBlockId, placeType: 'stickyBottom', announcementId: offer.offerId },
        { logger, httpApi },
      );
    },
    [siteBlockId, offer.offerId, httpApi, logger],
  );

  const handleCall = React.useCallback(async () => {
    if (!phone) {
      return;
    }

    const comagicPhone = await loadComagicPhone();

    if (!isCalltrackingEnabled) {
      trackPhoneButtonClick({ label: gaLabel, product });
    }

    if (!isLeadFactory) {
      produceCall(comagicPhone || phone);
    }

    if (onClick) {
      onClick();
    }
  }, [phone, loadComagicPhone, gaLabel, product, isCalltrackingEnabled, isLeadFactory, onClick, produceCall]);

  const trackPhoneClick = React.useCallback(() => {
    trackPhoneButtonClick({ label: gaLabel, product });
  }, [gaLabel, product]);
  const trackCloseCallTracking = React.useCallback(() => {
    trackCloseWarning(product);
  }, [product]);
  const trackAcceptCallTracking = React.useCallback(() => {
    trackAcceptWarning(product);
  }, [product]);
  const trackShowCallTrackingWarning = React.useCallback(() => {
    trackShowWarning(product);
  }, [product]);

  return React.useMemo(
    () => ({
      handleCall,
      loadComagicPhone,
      trackPhoneClick,
      trackCloseCallTracking,
      trackAcceptCallTracking,
      trackShowCallTrackingWarning,
      produceCall,
      phone,
      isLeadFactory,
      isCalltrackingEnabled,
    }),
    [
      loadComagicPhone,
      handleCall,
      trackPhoneClick,
      trackCloseCallTracking,
      trackAcceptCallTracking,
      trackShowCallTrackingWarning,
      produceCall,
      phone,
      isLeadFactory,
      isCalltrackingEnabled,
    ],
  );
}
